import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  CalendarClock,
  ChartColumnBig,
  Grid3x3,
  House,
  Mail,
  MessageCircleMore,
  MessageSquare,
  Phone,
  PhoneIncoming,
  Star,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import useRouterData from "@/hooks/api/useRouterData";
import usePipeline from "@/features/stage/api/usePipeline";
import { usePermissions } from "@/rbac/usePermissions";
import type { Stage } from "@/features/stage/types/stageTypes";

/**
 * The bottom bar's model (the mobile app's `navConfig.ts`): five tabs, and
 * for Leads / Chat / Dialer the segments the bar swaps to when the tab is
 * tapped. Everything that is role-dependent comes from the SAME `/platform/tabs`
 * answer the sidebar uses, so a role sees here exactly what it sees on the
 * desktop — never more.
 */
export type TabKey = "home" | "leads" | "chat" | "reports" | "dialer";

export interface Segment {
  key: string;
  label: string;
  icon: LucideIcon;
  /** Where the chip goes: a path, with `?seg=` for the fixed segments. */
  to: string;
}

export interface TabDef {
  key: TabKey;
  label: string;
  icon: LucideIcon;
  /** Where the tab goes when it has no segments. */
  to: string;
  segments: Segment[];
}

export const LEADS_SEGMENTS = {
  all: "/leads?seg=all",
  views: "/leads?seg=views",
  followups: "/leads?seg=followups",
} as const;

const SEG_MEMORY = "xm:seg:";

/** The segment the tab opens on: the last one used here, else its default. */
export function rememberedSegment(tab: TabKey, fallback: string): string {
  try {
    return sessionStorage.getItem(SEG_MEMORY + tab) || fallback;
  } catch {
    return fallback;
  }
}
export function rememberSegment(tab: TabKey, to: string): void {
  try {
    sessionStorage.setItem(SEG_MEMORY + tab, to);
  } catch {
    /* fine */
  }
}

interface RouteLike {
  key?: string;
  path?: string;
  label?: string;
  component?: string;
  stageId?: number | null;
  icon?: LucideIcon;
}

const clean = (p?: string) => (p ? (p.startsWith("/") ? p : `/${p}`) : "");

export function useMobileNav() {
  const { tabs, stageModules } = useRouterData();
  const { pipeline } = usePipeline();
  const { hasRouteKey, bypassesRbacChecks } = usePermissions();
  const location = useLocation();

  const model = useMemo(() => {
    const stageRoutes = ((stageModules ?? []) as RouteLike[]).filter((r) => r.stageId != null);
    const stageById = new Map<number, Stage>();
    for (const s of (pipeline ?? []) as Stage[]) stageById.set(s.id, s);
    const stageSegments: Segment[] = stageRoutes
      .map((r) => ({ route: r, stage: stageById.get(Number(r.stageId)) }))
      .sort((a, b) => (a.stage?.order ?? 999) - (b.stage?.order ?? 999))
      .map(({ route, stage }) => ({
        key: `stage-${route.stageId}`,
        label: stage?.name ?? route.label ?? "Stage",
        icon: route.icon ?? House,
        to: clean(route.path),
      }));

    const hasLeads = bypassesRbacChecks || hasRouteKey("leads") || stageRoutes.length > 0;
    const whatsappPath = clean(tabs?.whatsapp?.path);
    const gmailPath = clean(tabs?.gmail?.path);
    const reportPath = clean(tabs?.report?.path);

    const list: TabDef[] = [
      { key: "home", label: "Home", icon: House, to: "/", segments: [] },
    ];
    if (hasLeads) {
      list.push({
        key: "leads",
        label: "Leads",
        icon: Users,
        to: LEADS_SEGMENTS.followups,
        segments: [
          { key: "all", label: "All leads", icon: Users, to: LEADS_SEGMENTS.all },
          { key: "views", label: "Views", icon: Star, to: LEADS_SEGMENTS.views },
          { key: "followups", label: "Follow-ups", icon: CalendarClock, to: LEADS_SEGMENTS.followups },
          ...stageSegments,
        ],
      });
    }
    list.push({
      key: "chat",
      label: "Chat",
      icon: MessageSquare,
      to: whatsappPath ? "/chat?seg=whatsapp" : "/chat?seg=internal",
      segments: [
        ...(whatsappPath ? [{ key: "whatsapp", label: "WhatsApp", icon: MessageCircleMore, to: "/chat?seg=whatsapp" }] : []),
        { key: "internal", label: "Internal", icon: UsersRound, to: "/chat?seg=internal" },
        ...(gmailPath ? [{ key: "gmail", label: "Gmail", icon: Mail, to: gmailPath }] : []),
      ],
    });
    if (reportPath) {
      list.push({ key: "reports", label: "Reports", icon: ChartColumnBig, to: reportPath, segments: [] });
    }
    list.push({
      key: "dialer",
      label: "Dialer",
      icon: Phone,
      to: "/dialer?seg=keypad",
      segments: [
        { key: "keypad", label: "Keypad", icon: Grid3x3, to: "/dialer?seg=keypad" },
        { key: "recents", label: "Recents", icon: PhoneIncoming, to: "/dialer?seg=recents" },
      ],
    });
    return { tabs: list, whatsappPath, gmailPath, reportPath, stagePaths: new Set(stageSegments.map((s) => s.to)) };
  }, [tabs, stageModules, pipeline, hasRouteKey, bypassesRbacChecks]);

  // Which tab the current URL belongs to.
  const here = location.pathname + location.search;
  const activeTab = useMemo<TabKey | null>(() => {
    const p = location.pathname;
    if (p === "/") return "home";
    if (p === "/leads" || p.startsWith("/leads/") || p.startsWith("/views/") || model.stagePaths.has(p)) return "leads";
    if (p === "/chat" || (model.whatsappPath && p === model.whatsappPath) || (model.gmailPath && p.startsWith(model.gmailPath)))
      return "chat";
    if (model.reportPath && p.startsWith(model.reportPath)) return "reports";
    if (p === "/dialer") return "dialer";
    return null;
  }, [location.pathname, model]);

  const activeSegment = useMemo(() => {
    const tab = model.tabs.find((t) => t.key === activeTab);
    if (!tab) return null;
    const seg = new URLSearchParams(location.search).get("seg");
    const found =
      tab.segments.find((s) => s.to === here) ??
      tab.segments.find((s) => s.to === location.pathname) ??
      (seg ? tab.segments.find((s) => s.key === seg) : undefined) ??
      // A bare /leads (a drill, a notification) is the All leads list.
      (activeTab === "leads" && location.pathname === "/leads" ? tab.segments.find((s) => s.key === "all") : undefined) ??
      (activeTab === "chat" && location.pathname === "/chat" ? tab.segments[0] : undefined) ??
      (activeTab === "dialer" ? tab.segments[0] : undefined);
    return found ?? null;
  }, [model.tabs, activeTab, here, location.pathname, location.search]);

  return { ...model, activeTab, activeSegment };
}
